import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { V, serif, mono, eyebrow, card, btnGold, btnGhost, TIER2_SESSION_CODE } from '../theme'
import FeedbackCard from './FeedbackCard'
import CandlestickSimulator from '../CandlestickSimulator'
import MovingAverageSimulator from '../MovingAverageSimulator'

function ConfigView({ state, onUpdate, onNavigate }) {
  const [form, setForm] = React.useState({
    studentName:       state.studentName && state.studentName !== 'Trader' ? state.studentName : (localStorage.getItem('tlaw_student_name') || ''),
    accountSize:       String(state.accountSize || ''),
    currentBalance:    String(state.currentBalance || ''),
    dailyMaxLoss:      String(state.dailyMaxLoss || '5'),
    weeklyMaxLoss:     String(state.weeklyMaxLoss || '10'),
    defaultContracts:  String(state.defaultContracts || '1'),
    maxRiskPerTrade:   String(state.maxRiskPerTrade || '2'),
    innerWorkPrompts:  state.innerWorkPrompts || 'always',
    accountType:       state.accountType || 'cash',
  });
  const [saved, setSaved] = React.useState(false);

  const inpStyle = {
    background: '#18181c',
    border: '1px solid rgba(201,168,76,0.2)',
    borderRadius: '6px',
    padding: '10px 14px',
    color: '#f0ede6',
    fontSize: '14px',
    fontFamily: "'Jost',sans-serif",
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };
  const sectionCard = {
    background: V.bg2,
    border: '1px solid rgba(201,168,76,0.15)',
    borderRadius: '10px',
    padding: '20px',
    marginBottom: '20px',
  };
  const sectionLabel = {
    fontSize: '10px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: V.muted,
    fontFamily: "'Jost',sans-serif",
    marginBottom: '16px',
    display: 'block',
  };
  const fieldLabel = {
    display: 'block',
    fontSize: '13px',
    color: '#f0ede6',
    fontFamily: "'Jost',sans-serif",
    marginBottom: '4px',
  };
  const helpText = {
    fontSize: '11px',
    color: V.dim,
    fontFamily: "'Jost',sans-serif",
    marginTop: '5px',
    fontStyle: 'italic',
  };
  const fieldWrap = { marginBottom: '16px' };

  const handleSave = () => {
    const trimmedName = form.studentName.trim();
    if (trimmedName) {
      try { localStorage.setItem('tlaw_student_name', trimmedName); } catch {}
    }
    const acctSize = parseFloat(form.accountSize) || 0;
    // Sync accountSize → tlaw_risk_baseline if no baseline has been set yet
    if (acctSize > 0 && !localStorage.getItem('tlaw_risk_baseline')) {
      try { localStorage.setItem('tlaw_risk_baseline', String(acctSize)); } catch {}
    }
    onUpdate && onUpdate({
      studentName:      trimmedName || state.studentName,
      accountSize:      acctSize,
      currentBalance:   parseFloat(form.currentBalance) || 0,
      dailyMaxLoss:     parseFloat(form.dailyMaxLoss) || 5,
      weeklyMaxLoss:    parseFloat(form.weeklyMaxLoss) || 10,
      defaultContracts: parseInt(form.defaultContracts) || 1,
      maxRiskPerTrade:  parseFloat(form.maxRiskPerTrade) || 2,
      innerWorkPrompts: form.innerWorkPrompts,
      accountType:      form.accountType,
    });
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      onNavigate('dashboard');
    }, 3000);
  };

  return (
    <div style={{ padding: '40px 24px', maxWidth: '640px', margin: '0 auto', boxSizing: 'border-box' }}>
      <h1 style={{ ...serif, fontSize: '32px', fontWeight: 400, color: V.gold, margin: '0 0 8px', textAlign: 'center' }}>Your Trading Setup</h1>
      <p style={{ fontSize: '14px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', lineHeight: 1.7, marginBottom: '32px', textAlign: 'center' }}>
        Set these once. Update them as your account grows.
      </p>

      {/* SECTION 0 — PROFILE */}
      <div style={sectionCard}>
        <span style={sectionLabel}>Profile</span>
        <div style={fieldWrap}>
          <label style={fieldLabel}>Your First Name</label>
          <p style={{ ...helpText, marginBottom: '8px', marginTop: 0 }}>Used to personalize your experience and your feedback.</p>
          <input type="text" placeholder="Your first name" value={form.studentName}
            onChange={e => setForm(f => ({ ...f, studentName: e.target.value }))}
            onFocus={e => e.target.style.border = '1px solid #c9a84c'}
            onBlur={e => e.target.style.border = '1px solid rgba(201,168,76,0.2)'}
            style={inpStyle} />
        </div>
      </div>

      {/* SECTION 1 — ACCOUNT */}
      <div style={sectionCard}>
        <span style={sectionLabel}>Account</span>

        {/* Account Type */}
        <div style={fieldWrap}>
          <label style={fieldLabel}>Account Type</label>
          <p style={{ ...helpText, marginBottom: '10px', marginTop: 0 }}>This affects how you should think about your buying power and risk.</p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[{ v: 'cash', l: 'Cash Account' }, { v: 'margin', l: 'Margin Account' }, { v: 'portfolio', l: 'Portfolio Margin' }].map(({ v, l }) => (
              <button key={v} onClick={() => setForm(f => ({ ...f, accountType: v }))}
                style={{ flex: '1 1 auto', padding: '9px 12px', borderRadius: '20px', border: `1px solid ${form.accountType === v ? V.gold : 'rgba(201,168,76,0.2)'}`, background: form.accountType === v ? 'rgba(201,168,76,0.15)' : V.bg3, color: form.accountType === v ? V.gold : V.muted, fontSize: '12px', cursor: 'pointer', fontFamily: "'Jost',sans-serif", minHeight: '44px' }}>
                {l}
              </button>
            ))}
          </div>
          {form.accountType === 'margin' && (
            <div style={{ marginTop: '12px', padding: '14px 16px', background: 'rgba(201,168,76,0.08)', borderLeft: `3px solid ${V.gold}`, borderRadius: '8px' }}>
              <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.8, margin: '0 0 10px' }}>
                Important: With a margin account your buying power is higher than your actual account value because your broker is lending you additional capital.
              </p>
              <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.8, margin: '0 0 10px' }}>
                For risk calculations always use your Net Liquidation Value — not your buying power. Net Liquidation Value is what your account is actually worth if all positions were closed today.
              </p>
              <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.8, margin: 0 }}>
                Find your Net Liquidation Value in ThinkorSwim at the top of your account page. Update it here regularly as your account grows.
              </p>
            </div>
          )}
          {form.accountType === 'portfolio' && (
            <div style={{ marginTop: '12px', padding: '14px 16px', background: 'rgba(201,168,76,0.08)', borderLeft: `3px solid ${V.gold}`, borderRadius: '8px' }}>
              <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.8, margin: '0 0 10px' }}>
                Professional-level margin status. Risk is calculated across your entire portfolio simultaneously. Requires broker approval and typically $125,000+ account size.
              </p>
              <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.8, margin: 0 }}>
                Use <strong style={{ color: V.text }}>Available Dollars</strong> as your baseline. In ThinkorSwim: Monitor tab → Balances → Available Dollars. This is the number that actually moves when you trade.
              </p>
            </div>
          )}
        </div>

        <div style={fieldWrap}>
          <label style={fieldLabel}>
            {form.accountType === 'portfolio' ? 'Available Dollars ($)' : 'Total Account Value (Net Liq) ($)'}
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: V.muted, fontSize: '14px', flexShrink: 0 }}>$</span>
            <input type="number" placeholder="e.g. 25000" value={form.accountSize}
              onChange={e => setForm(f => ({ ...f, accountSize: e.target.value }))}
              onFocus={e => e.target.style.border = '1px solid #c9a84c'}
              onBlur={e => e.target.style.border = '1px solid rgba(201,168,76,0.2)'}
              style={inpStyle} />
          </div>
          <div style={helpText}>
            {form.accountType === 'portfolio'
              ? 'In ThinkorSwim: Monitor tab → Balances → Available Dollars. This is the number that actually moves when you trade.'
              : 'In ThinkorSwim: Monitor tab → Balances → Net Liquidating Value. Used to track your account growth over time.'}
          </div>
        </div>

        <div style={fieldWrap}>
          <label style={fieldLabel}>Options Buying Power ($)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: V.muted, fontSize: '14px', flexShrink: 0 }}>$</span>
            <input type="number" placeholder="e.g. 25000" value={form.currentBalance}
              onChange={e => setForm(f => ({ ...f, currentBalance: e.target.value }))}
              onFocus={e => e.target.style.border = '1px solid #c9a84c'}
              onBlur={e => e.target.style.border = '1px solid rgba(201,168,76,0.2)'}
              style={inpStyle} />
          </div>
          <div style={helpText}>In ThinkorSwim: Monitor tab → Balances → Options Buying Power. This is what you can actually deploy in new trades right now. Used for position sizing.</div>
        </div>

        <div style={fieldWrap}>
          <label style={fieldLabel}>Max Risk Per Trade (%)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="number" min="1" max="5" step="0.5" value={form.maxRiskPerTrade}
              onChange={e => setForm(f => ({ ...f, maxRiskPerTrade: e.target.value }))}
              onFocus={e => e.target.style.border = '1px solid #c9a84c'}
              onBlur={e => e.target.style.border = '1px solid rgba(201,168,76,0.2)'}
              style={inpStyle} />
            <span style={{ color: V.muted, fontSize: '14px', flexShrink: 0 }}>%</span>
          </div>
          {(() => {
            const bp = parseFloat(form.currentBalance) || 0;
            const pct = parseFloat(form.maxRiskPerTrade) || 2;
            const dollar = bp > 0 ? `At your $${bp.toLocaleString()} buying power: ${pct}% = $${(bp * pct / 100).toFixed(0)} maximum risk on any single trade.` : null;
            return (
              <div style={helpText}>
                Recommended: 1-2% of your Options Buying Power per trade. At $25,000 buying power: 2% = $500 maximum risk on any single trade.
                {dollar && <div style={{ color: V.sage, marginTop: '4px', fontStyle: 'normal', fontWeight: 500 }}>{dollar}</div>}
              </div>
            );
          })()}
        </div>

        <div style={fieldWrap}>
          <label style={fieldLabel}>Daily Max Loss (%)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="number" min="1" max="10" step="0.5" value={form.dailyMaxLoss}
              onChange={e => setForm(f => ({ ...f, dailyMaxLoss: e.target.value }))}
              onFocus={e => e.target.style.border = '1px solid #c9a84c'}
              onBlur={e => e.target.style.border = '1px solid rgba(201,168,76,0.2)'}
              style={inpStyle} />
            <span style={{ color: V.muted, fontSize: '14px', flexShrink: 0 }}>%</span>
          </div>
          {(() => {
            const bp = parseFloat(form.currentBalance) || 0;
            const pct = parseFloat(form.dailyMaxLoss) || 5;
            const dollar = bp > 0 ? `At your $${bp.toLocaleString()} buying power: ${pct}% = $${(bp * pct / 100).toFixed(0)}.` : null;
            return (
              <div style={helpText}>
                Recommended: 5% of your Options Buying Power. When you hit this number — stop trading for the day. No exceptions.
                {dollar && <div style={{ color: V.sage, marginTop: '4px', fontStyle: 'normal', fontWeight: 500 }}>{dollar}</div>}
              </div>
            );
          })()}
        </div>

        <div style={fieldWrap}>
          <label style={fieldLabel}>Weekly Max Loss (%)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="number" min="3" max="20" step="1" value={form.weeklyMaxLoss}
              onChange={e => setForm(f => ({ ...f, weeklyMaxLoss: e.target.value }))}
              onFocus={e => e.target.style.border = '1px solid #c9a84c'}
              onBlur={e => e.target.style.border = '1px solid rgba(201,168,76,0.2)'}
              style={inpStyle} />
            <span style={{ color: V.muted, fontSize: '14px', flexShrink: 0 }}>%</span>
          </div>
          {(() => {
            const bp = parseFloat(form.currentBalance) || 0;
            const pct = parseFloat(form.weeklyMaxLoss) || 10;
            const dollar = bp > 0 ? `At your $${bp.toLocaleString()} buying power: ${pct}% = $${(bp * pct / 100).toFixed(0)}.` : null;
            return (
              <div style={helpText}>
                Recommended: 10% of your Options Buying Power. A circuit breaker for difficult weeks. Stop, review, reset before continuing.
                {dollar && <div style={{ color: V.sage, marginTop: '4px', fontStyle: 'normal', fontWeight: 500 }}>{dollar}</div>}
              </div>
            );
          })()}
        </div>

        <div style={fieldWrap}>
          <label style={fieldLabel}>Default Contracts Per Trade</label>
          <input type="number" min="1" max="100" step="1" value={form.defaultContracts}
            onChange={e => setForm(f => ({ ...f, defaultContracts: e.target.value }))}
            onFocus={e => e.target.style.border = '1px solid #c9a84c'}
            onBlur={e => e.target.style.border = '1px solid rgba(201,168,76,0.2)'}
            style={inpStyle} />
          <div style={helpText}>Your typical starting position size.</div>
        </div>
      </div>

      {/* SECTION 2 — INNER WORK */}
      <div style={sectionCard}>
        <span style={sectionLabel}>Inner Work</span>
        <label style={{ ...fieldLabel, marginBottom: '12px' }}>How often would you like to be invited to reflect before and after trades?</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[{ v: 'always', l: 'Always' }, { v: 'once_daily', l: 'Once Daily' }, { v: 'never', l: 'Never' }].map(({ v, l }) => (
            <button key={v} onClick={() => setForm(f => ({ ...f, innerWorkPrompts: v }))}
              style={{ flex: 1, padding: '9px 12px', borderRadius: '20px', border: `1px solid ${form.innerWorkPrompts === v ? V.gold : 'rgba(201,168,76,0.2)'}`, background: form.innerWorkPrompts === v ? 'rgba(201,168,76,0.15)' : V.bg3, color: form.innerWorkPrompts === v ? V.gold : V.muted, fontSize: '12px', cursor: 'pointer', fontFamily: "'Jost',sans-serif", minHeight: '44px' }}>
              {l}
            </button>
          ))}
        </div>
        <p style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', marginTop: '10px', lineHeight: 1.6 }}>
          You can change this any time.
        </p>
      </div>

      {/* ADMIN SECTION — only shown in admin mode */}
      {state.adminMode && (
        <div style={{ ...sectionCard, borderColor: 'rgba(196,122,122,0.3)', background: 'rgba(196,122,122,0.04)' }}>
          <span style={{ ...sectionLabel, color: V.rose }}>Admin — Tier 2 Access</span>
          <div style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif", marginBottom: '8px' }}>Session Unlock Code</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <code style={{ flex: 1, padding: '10px 14px', background: V.bg3, border: `0.5px solid rgba(196,122,122,0.3)`, borderRadius: '7px', fontSize: '15px', fontWeight: 600, color: V.goldL, fontFamily: 'monospace', letterSpacing: '0.1em' }}>
                {TIER2_SESSION_CODE}
              </code>
            </div>
            <div style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif", marginTop: '6px', fontStyle: 'italic' }}>
              Share this code with students you want to unlock Tier 2. Code resets on page refresh.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '13px', color: V.text, fontFamily: "'Jost',sans-serif" }}>Tier 2 Status</div>
              <div style={{ fontSize: '11px', color: state.tier2Unlocked ? V.sage : V.muted, fontFamily: "'Jost',sans-serif", marginTop: '2px' }}>
                {state.tier2Unlocked ? 'Unlocked — student has Tier 2 access' : 'Locked — students see teaser cards only'}
              </div>
            </div>
            <button
              onClick={() => onUpdate && onUpdate({ tier2Unlocked: !state.tier2Unlocked })}
              style={{ padding: '8px 16px', background: state.tier2Unlocked ? 'rgba(196,122,122,0.1)' : 'rgba(122,171,138,0.1)', border: `0.5px solid ${state.tier2Unlocked ? 'rgba(196,122,122,0.4)' : 'rgba(122,171,138,0.4)'}`, borderRadius: '7px', color: state.tier2Unlocked ? V.rose : V.sage, fontSize: '12px', cursor: 'pointer', fontFamily: "'Jost',sans-serif" }}>
              {state.tier2Unlocked ? 'Lock Tier 2' : 'Unlock Tier 2'}
            </button>
          </div>
        </div>
      )}

      {saved && (
        <div style={{ background: 'rgba(122,171,138,0.1)', border: `0.5px solid rgba(122,171,138,0.4)`, borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: V.sage, fontFamily: "'Jost',sans-serif", lineHeight: 1.5 }}>
          ✓ Settings saved.
        </div>
      )}

      <button style={{ ...btnGold, width: '100%', height: '48px', fontSize: '14px', marginBottom: '12px' }} onClick={handleSave}>
        Save My Settings →
      </button>
      <button style={{ ...btnGhost, width: '100%', padding: '11px', fontSize: '13px' }} onClick={() => onNavigate('dashboard')}>
        ← Back to Dashboard
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SIMULATOR WRAPPERS (add FeedbackCard below external simulators)
// ═══════════════════════════════════════════════════════════════
function CandleSimWrapper({ onBack, onXpAward, onNavigate }) {
  const [isComplete, setIsComplete] = React.useState(false);
  return (
    <div>
      <CandlestickSimulator onXpAward={onXpAward} onBack={onBack} onNavigate={onNavigate} onAllComplete={() => setIsComplete(true)} />
      {isComplete && (
        <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 24px 40px' }}>
          <FeedbackCard chapterId='practice_candles' chapterTitle='Candlesticks Practice' type='practice' />
        </div>
      )}
    </div>
  );
}
function MASimWrapper({ onBack, xpAward }) {
  return (
    <div>
      <MovingAverageSimulator xpAward={xpAward} onBack={onBack} />
      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 24px 40px' }}>
        <FeedbackCard chapterId='practice_ma' chapterTitle='Moving Averages Practice' type='practice' />
      </div>
    </div>
  );
}

export { CandleSimWrapper, MASimWrapper }
export default ConfigView
